// @ts-nocheck
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Mail } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { api } from '../lib/api';
import { useYears } from '../lib/useYears';
import { formatCurrency, daysSince } from '../lib/utils';

export function ClientList() {
  const { years, year: defaultYear } = useYears();
  const [clients, setClients] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('missing_amount');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Set year once available years are loaded
  useEffect(() => {
    if (defaultYear && !year) setYear(String(defaultYear));
  }, [defaultYear]);

  const fetchClients = () => {
    if (!year) return;
    setLoading(true);
    const params: Record<string, string> = { year, sort, order, page: String(page), limit: '50' };
    if (search) params.search = search;
    if (status) params.status = status;
    api.clients(params).then((res) => {
      setClients(res.clients);
      setTotal(res.total);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, [year, status, sort, order, page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchClients();
  };

  const toggleSort = (field: string) => {
    if (sort === field) setOrder(o => o === 'desc' ? 'asc' : 'desc');
    else { setSort(field); setOrder('desc'); }
  };

  const toggleSelect = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === clients.length) setSelected(new Set());
    else setSelected(new Set(clients.map(c => c.id as number)));
  };

  const handleBulkEmail = async () => {
    if (selected.size === 0) return;
    try {
      await api.bulkSendEmail([...selected], undefined, year);
      const scope = year === 'all' ? 'all years' : year;
      alert(`Email requests sent to ${selected.size} clients (${scope})`);
      setSelected(new Set());
      fetchClients();
    } catch (err) {
      alert(String((err as Error).message));
    }
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th
      onClick={() => toggleSort(field)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
    >
      <span className="flex items-center gap-1">
        {children}
        {sort === field && (order === 'asc' ? '\u2191' : '\u2193')}
      </span>
    </th>
  );

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Clients</h1>
        <div className="flex items-center gap-3">
          {selected.size > 0 && (
            <button
              onClick={handleBulkEmail}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              <Mail size={14} /> Email {selected.size} clients
            </button>
          )}
          <select value={year} onChange={e => { setYear(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm">
            <option value="all">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by client name..."
            className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          />
        </form>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2.5 text-sm">
          <option value="">All statuses</option>
          <option value="none">No certificates</option>
          <option value="partial">Partial</option>
          <option value="complete">Complete</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={selected.size === clients.length && clients.length > 0} onChange={toggleAll} className="rounded border-gray-300" />
                </th>
                <SortHeader field="name">Client</SortHeader>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Contact</th>
                <SortHeader field="total_amount">Total WHT</SortHeader>
                <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Certificates</th>
                <SortHeader field="completion">Completion</SortHeader>
                <SortHeader field="missing_amount">Missing Amount</SortHeader>
                <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Last Email</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {clients.map((c) => {
                const days = daysSince(c.last_email_date as string | null);
                const pct = Number(c.completion_pct || 0);
                return (
                  <tr key={c.id as number} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(c.id as number)} onChange={() => toggleSelect(c.id as number)} className="rounded border-gray-300" />
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/wht/clients/${c.id}`} className="text-blue-600 hover:underline font-medium">
                        {c.name as string}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.contact_email ? (
                        <span>{c.contact_email as string}</span>
                      ) : (
                        <span className="text-red-400">No email</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{formatCurrency(Number(c.total_amount))}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-green-600 font-medium">{c.received_count as number}</span>
                      <span className="text-gray-400"> / </span>
                      <span>{c.total_transactions as number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                          <div
                            className={`h-2 rounded-full ${pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-blue-500' : 'bg-red-300'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-red-600">
                      {Number(c.missing_amount) > 0 ? formatCurrency(Number(c.missing_amount)) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-xs">
                      {days !== null ? (
                        <span className={days > 14 ? 'text-red-500' : 'text-gray-500'}>{days}d ago</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && clients.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    No clients found. Import data to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
            <span className="text-gray-500">{total} clients total</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
              <span className="px-3 py-1">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
